// Hideo - Sources page (Collections › Sources, src/pages/SourcesPage.vue): sites, papers, TV programmes… of the active
// project. Each source has a category (what kind of source it is: fixed ones plus the user's own, ./categories.ts) and
// per-project tags for the common themes (src/features/tags.ts). One big button per category that has sources (+ "All
// sources", "No category"); tapping one opens the compact list of its sources, with search; the tag row above filters
// the buttons and the list. The source dialog is src/components/dialogs/SourceDialog.vue.
import { normUrl, ytThumb } from '../utils/url'
import { sameTag } from '../data/tags'
import { t } from '../i18n'
import { KIND_NONE, allKinds, guessSourceKind, ofKind } from './categories'
export { guessSourceKind }
import type { ProfileData, Source } from '../data/types'
import { TAG_NONE, barTagsOf, ofTag } from './tags'
import { ofProject, validProject } from './projects'
import { firstLine, putItem, removeItem, textMatches } from '../data/items'

/** Open tile: '' = none, SRC_ALL = every source, SRC_NONE = sources without a category, otherwise a category id */
export const SRC_ALL = '*'
export const SRC_NONE = KIND_NONE
/** The open tile is a real category (not "All sources", "No category" or none) */
export const isKindTab = (tab: string): boolean => !!tab && tab !== SRC_ALL && tab !== SRC_NONE

/** The source's link ('' without one; '#' was the old "no link") */
export const sourceUrl = (s: Pick<Source, 'url'>): string => (s.url && s.url !== '#' ? normUrl(s.url) : '')
/** Site of the link without "www." ('' without a valid link) */
export function sourceDomain(s: Pick<Source, 'url'>): string {
  try {
    return new URL(sourceUrl(s)).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}
/** Icon of a site (Google's favicon service) */
export const faviconUrl = (dom: string): string => `https://www.google.com/s2/favicons?domain=${encodeURIComponent(dom)}&sz=64`
/** Image of a source in the list: its own thumbnail, otherwise the YouTube one ('' = the site icon is shown instead) */
export const sourceThumb = (s: Pick<Source, 'url' | 'thumbnail'>): string => s.thumbnail || ytThumb(sourceUrl(s))

/** Sources of the active project ('all' = every source) */
export const projectSources = (d: ProfileData, active: string): Source[] => ofProject(d.sources, active)

/** Sources of a tile */
export const sourcesOfTab = (sources: Source[], tab: string): Source[] => (tab === SRC_ALL ? sources : ofKind(sources, tab))

// ===== Tag row (common themes: '' = all, TAG_NONE = without tags, otherwise a tag name, compared without case) =====
interface SourceTagChip {
  /** '' = "All", TAG_NONE = "No tag", otherwise the tag */
  key: string
  count: number
}
/** The tag filter, still valid (with the spelling of the bar): a tag no longer in the bar goes back to "All" */
const validSourceTag = (tags: string[], tag: string): string => (!tag || tag === TAG_NONE ? tag : tags.find(x => sameTag(x, tag)) || '')

export interface SourceTile {
  /** SRC_ALL, SRC_NONE or the category id */
  key: string
  kind: 'all' | 'none' | 'cat'
  name: string
  icon: string
  /** An own category (not a fixed one) */
  own: boolean
  count: number
  /** Sites of its sources, without repetitions (the first 4 are shown as icons, then "+N") */
  domains: string[]
}

interface SourcesView {
  /** The sources of the shown projects, after the tag filter */
  sources: Source[]
  /** The tags of the bar */
  tags: string[]
  /** The tag row: "All", one pill per tag, "No tag" (only with untagged sources); empty without tags */
  chips: SourceTagChip[]
  /** The tag filter, still valid */
  tag: string
  /** "All sources", one per category that has sources (fixed ones first, in their order), "No category" */
  tiles: SourceTile[]
}

/** The big buttons and the tag row of the page; everything after the tag filter (tag) */
export function sourcesView(d: ProfileData, active: string, tag = ''): SourcesView {
  const inProject = projectSources(d, active), tags = barTagsOf(d, 'src', active)
  tag = validSourceTag(tags, tag)
  const sources = ofTag(inProject, tag)
  const none = ofTag(inProject, TAG_NONE).length
  const chips = tags.length ? [{ key: '', count: inProject.length }, ...tags.map(x => ({ key: x, count: ofTag(inProject, x).length })),
    ...(none ? [{ key: TAG_NONE, count: none }] : [])] : []
  const tile = (key: string, kind: SourceTile['kind'], name: string, icon: string, own = false): SourceTile => {
    const list = sourcesOfTab(sources, key)
    return { key, kind, name, icon, own, count: list.length, domains: [...new Set(list.map(sourceDomain).filter(Boolean))] }
  }
  const cats = allKinds(d, 'src').map(k => tile(k.id, 'cat', k.name, k.icon, !k.fixed)).filter(x => x.count)
  const untyped = ofKind(sources, KIND_NONE).length
  return {
    sources, tags, chips, tag,
    tiles: [tile(SRC_ALL, 'all', t('All sources'), 'fa-layer-group'), ...cats,
      ...(untyped && cats.length ? [tile(SRC_NONE, 'none', t('No category'), 'fa-shapes')] : [])]
  }
}

/** Search on title, link, note (mentions as plain text, through `plain`), tags and category; q already lower case */
export const matchesSource = (s: Source, q: string, plain: (text: string) => string, kindName = ''): boolean =>
  textMatches(q, s.title, sourceUrl(s), plain(s.note || ''), ...(s.tags || []), kindName)

/** List of the open tile, by title; `all` = before the search (to tell "no results" from "no sources") */
export function sourceList(sources: Source[], tab: string, query: string, plain: (text: string) => string, locale: string,
  kindName: (s: Source) => string = () => ''): { all: Source[]; list: Source[] } {
  const all = sourcesOfTab(sources, tab).slice().sort((a, b) => (a.title || '').localeCompare(b.title || '', locale))
  const q = query.trim().toLowerCase()
  return { all, list: q ? all.filter(s => matchesSource(s, q, plain, kindName(s))) : all }
}

/** Tags shown on a row: all of them, except the tag of the filter */
export const otherTags = (s: Source, tag: string): string[] => (s.tags || []).filter(x => !(tag && tag !== TAG_NONE && sameTag(x, tag)))

/** The open tile, still valid: a category without sources (after the tag filter) closes it */
export const validTab = (view: Pick<SourcesView, 'tiles'>, tab: string): string => (tab && !view.tiles.some(x => x.key === tab) ? '' : tab)

// ===== Source dialog (SourceDialog.vue) =====
/** The fields of the source dialog */
interface SourceForm {
  title: string
  tags: string[]
  /** Category ('' = none; src/features/categories.ts) */
  kind: string
  projectId: string
  /** As typed: "https://" is added on save ('' = no link) */
  url: string
  /** '' | "media:<id>" | an image address (ImageBox.vue) */
  thumbnail: string
  /** With @mentions as "@[type:id]" (src/features/mentions.ts) */
  note: string
}

/** A link or text dropped on the Sources page (src/stores/drop.ts): a new source starts with it, its title selected */
export interface SourcePrefill {
  title?: string
  url?: string
}

/** The dialog's starting fields: those of the source, or empty for a new one, with the tag chosen on the Sources page
 *  (filter), the category open there (kind) and the project of new items (newProject) */
export function sourceForm(d: ProfileData, id: string | null, filter: string, newProject: string, kind = ''): SourceForm {
  const s = id ? d.sources.find(x => x.id === id) : null
  if (s) {
    return { title: s.title || '', tags: [...(s.tags || [])], kind: s.kind || '', projectId: validProject(d, s.projectId, newProject),
      url: s.url && s.url !== '#' ? s.url : '', thumbnail: s.thumbnail || '', note: s.note || '' }
  }
  return { title: '', tags: filter ? [filter] : [], kind, projectId: newProject, url: '', thumbnail: '', note: '' }
}

/** What a drop on the Sources page fills in: a web address becomes the link, with its site (without "www.") as the
 *  title; any other text becomes the title (first line, 100 characters at most) */
export function droppedSource(text: string): SourcePrefill {
  const first = firstLine(text)
  if (!/^https?:\/\//i.test(first)) return { title: first.slice(0, 100), url: '' }
  try {
    return { title: new URL(first).hostname.replace(/^www\./, ''), url: first }
  } catch {
    return { title: '', url: first }
  }
}

/** Saves the dialog into the data: a new source (id null) or the source with that id, replaced by the dialog's fields
 *  (title and note trimmed, "https://" added to the link). null when the title is empty (nothing changes).
 *  created = a new source. The caller saves. */
export function saveSource(d: ProfileData, id: string | null, f: SourceForm, now = Date.now()): { source: Source; created: boolean } | null {
  const title = f.title.trim()
  if (!title) return null
  const source: Source = { id: id || 's_' + now, title, tags: [...f.tags], kind: f.kind, projectId: f.projectId, url: normUrl(f.url), thumbnail: f.thumbnail, note: f.note.trim() }
  return { source, created: putItem(d.sources, source, !!id) }
}

/** Removes a source from the data (true if it was there). The caller saves. */
export function deleteSource(d: ProfileData, id: string): boolean {
  return removeItem(d, 'sources', id)
}
